import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { CheckCircle } from 'lucide-react';
import { useOnboardingSteps } from '@/hooks/use-onboarding-steps';

interface OnboardingStepperProps {
  currentStep: number;
  className?: string;
}

/**
 * Componente que mostra o progresso do onboarding através de um stepper horizontal
 */
export const OnboardingStepper = ({ currentStep, className }: OnboardingStepperProps) => {
  const { steps } = useOnboardingSteps();
  
  // Calcular quais etapas estão completas (todas anteriores à atual)
  const completedSteps = useMemo(() => {
    return steps.map((_, index) => index < currentStep);
  }, [steps, currentStep]);

  return (
    <div className={cn("w-full mb-8", className)}>
      <div className="px-2 pb-4">
        {/* Versão simplificada para telas pequenas - mostrar apenas círculos com números */}
        <div className="sm:hidden w-full flex justify-between items-center">
          {steps.map((step, index) => {
            const isActive = index === currentStep;
            const isCompleted = completedSteps[index];
            
            return (
              <div key={`mobile-${step.id}`} className="flex flex-col items-center">
                <div 
                  className={cn(
                    "flex items-center justify-center w-8 h-8 rounded-full text-xs font-medium transition-colors", 
                    isActive && !isCompleted ? "bg-primary text-primary-foreground" : "",
                    isCompleted ? "bg-green-100 text-green-700 border-2 border-green-500" : "",
                    !isActive && !isCompleted ? "bg-muted text-muted-foreground" : ""
                  )}
                >
                  {isCompleted ? <CheckCircle className="h-4 w-4" /> : index + 1}
                </div>
                
                {/* Exibir apenas para o passo ativo em mobile */}
                {isActive && (
                  <p className="mt-1 text-xs font-medium text-primary line-clamp-1 max-w-[60px] text-center">
                    {step.title}
                  </p>
                )}
              </div>
            );
          })}
          
          {/* Barra de progresso sob os círculos */}
          <div className="absolute left-4 right-4 h-0.5 bg-muted -z-10 top-4" />
        </div>
        
        {/* Versão completa para telas maiores */}
        <div className="hidden sm:flex sm:justify-between sm:items-start w-full">
          {steps.map((step, index) => {
            const isActive = index === currentStep;
            const isCompleted = completedSteps[index];
            
            // Esta é uma versão simplificada para caber na tela
            return (
              <div key={`desktop-${step.id}`} className="flex flex-col items-center relative">
                <div 
                  className={cn(
                    "flex items-center justify-center w-10 h-10 rounded-full text-sm font-medium transition-colors z-10", 
                    isActive && !isCompleted ? "bg-primary text-primary-foreground" : "",
                    isCompleted ? "bg-green-100 text-green-700 border-2 border-green-500" : "",
                    !isActive && !isCompleted ? "bg-muted text-muted-foreground" : ""
                  )}
                >
                  {isCompleted ? <CheckCircle className="h-5 w-5" /> : index + 1}
                </div>
                <p className={cn(
                  "mt-2 text-xs font-medium line-clamp-1", 
                  isActive ? "text-primary" : 
                  isCompleted ? "text-green-700" : "text-muted-foreground"
                )}>
                  {step.title}
                </p>
                <p className="text-[10px] text-muted-foreground max-w-[80px] text-center line-clamp-2 h-8">
                  {step.description}
                </p>
              </div>
            );
          })}
          
          {/* Linha de progresso conectando os pontos */}
          <div className="absolute left-0 right-0 h-0.5 bg-muted top-5 -z-10 mx-12" />
          <div 
            className="absolute left-0 h-0.5 bg-primary top-5 -z-10 mx-12 transition-all duration-500"
            style={{ 
              width: `${(currentStep / (steps.length - 1)) * 100}%`,
              maxWidth: 'calc(100% - 4rem)'
            }} 
          />
        </div>
      </div>
    </div>
  );
}; 